import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { observer } from "mobx-react";
import ContextMenu from "../ContextMenu";
import { useContextMenuController, ContextMenuHandlers } from "./useContextMenuController";
import { AppointmentDto } from "../../../api-client";
import { TimeSlotAttendanceDialog } from "../../../components/PatientOverview/RxInfo/TerminView";
import { CancelAppointmentDialog } from "../../CancelAppointmentDialog";
import WaitList from "../../../components/ScheduleOverview/WaitList/WaitList";
import WarningDialog from "../../../atoms/WarningDialog";
import AppointmentEditCard from "../../../components/ScheduleOverview/Appointments/AppointmentEditCard";
import AppointmentOverviewModal from "../../../components/PatientOverview/RxInfo/AppointmentOverviewModal";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import useStore from "../../../helpers/useStore";
import { CalendarEvent, ClientTimeSlot } from "../controller/adapters";
import EditCancellationDialog from "../../../components/ScheduleOverview/Appointments/CancelAppointmentEditCard";
import { useCachedAppointmentLookup } from "../../../stores/Calendar/calendar.queries";
import { SetInformedDialog } from "../../../components/PatientInforming/SetInformedDialog";
import EditEventTitleDialog from "../EditEventTitleDialog";
import { slotTherapy } from "../../../helpers/slotTherapy";

export interface ContextMenuControllerRef {
  openContextMenu: (calendarEvent: CalendarEvent, x: number, y: number) => void;
  closeContextMenu: () => void;
  handleAttended: (timeSlot: ClientTimeSlot) => void;
  handleAppointmentOverview: (patientId: number,rxId?:string) => void;
  handleEditAppointment: (timeSlot: ClientTimeSlot) => void;
  onOpenNotesDialog: () => void;
  onCancelAppointment: () => void;
  /** Umziehen: remove the event(s) from the calendar and lift them onto the pinboard. */
  moveEventToShelf: (calendarEvent: CalendarEvent) => void;
}

interface ContextMenuWithControllerProps {
  handlers: ContextMenuHandlers;
  clickedTS: any; // TODO: type this properly
}
export const ContextMenuWithController = observer(forwardRef<
  ContextMenuControllerRef,
  ContextMenuWithControllerProps
>(({ handlers, clickedTS }, ref) => {
  const CalendarStore = useStore(CalendarContext);
  const controller = useContextMenuController(handlers);
  const [informedSlot, setInformedSlot] = useState<ClientTimeSlot | null>(null);
  
  // Use our new cached appointment lookup hook
  const { lookupByAppointmentId, debugInfo } = useCachedAppointmentLookup();

  // Debug instrumentation for visibility issues
  useEffect(() => {
    if (controller.isOpen) {
      // console.debug("[ContextMenuWithController] open render", {
        // isOpen: controller.isOpen,
        // mousePosition: controller.mousePosition,
        // event: controller.contextMenuEvent,
        // timeSlot: controller.clickedTimeSlot,
      // });
    }
  }, [
    controller.isOpen,
    controller.mousePosition,
    controller.contextMenuEvent,
    controller.clickedTimeSlot,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      openContextMenu: controller.openContextMenu,
      closeContextMenu: controller.closeContextMenu,
      handleAttended: controller.handleAttended,
      handleAppointmentOverview: controller.handleAppointmentOverview,
      handleEditAppointment: controller.handleEditAppointment,
      setShowNotesDialog: controller.setShowNotesDialog,
      onOpenNotesDialog: () => controller.setShowNotesDialog(true),
      onCancelAppointment: () => {
        if (controller.selectedAppointmentRef.current) {
          controller.handleCancelAppointment(controller.selectedAppointmentRef.current);
        }
      },
      moveEventToShelf: controller.handleMoveEvent,
    }),
    [controller.openContextMenu, controller.closeContextMenu, controller.handleAttended, controller.handleAppointmentOverview, controller.handleEditAppointment, controller.setShowNotesDialog, controller.handleCancelAppointment, controller.selectedAppointmentRef, controller.handleMoveEvent]
  );

  // Only resolve the appointment from cache when a dialog that actually consumes
  // it is open. Computing it on every render (the common, no-dialog case) is
  // wasteful and produced a stream of "[useCachedAppointmentLookup] Appointment
  // not found in any cache" warnings whenever a stale selection's id was no
  // longer cached. Gating to the consuming dialogs keeps the warning meaningful.
  const needsLookedUpApp =
    controller.dialogOpen ||
    controller.isCancellationDialogOpen ||
    controller.isAppointmentEditOpen ||
    controller.isEditCancellationDialogOpen;
  const lookedUpApp =
    needsLookedUpApp && controller.selectedAppointmentRef.current
      ? lookupByAppointmentId(controller.selectedAppointmentRef.current.appointmentId!)
      : undefined;

  // Snapshot the appointment for the cancellation dialog the moment it opens.
  // The dialog is otherwise bound to a live cache read (`lookedUpApp`), so any
  // background cache churn while the user is mid-edit (a websocket broadcast, a
  // delayed refetch, or an optimistic calendar update) can momentarily drop the
  // appointment, which would yank `lookedUpApp` to undefined and unmount the
  // open dialog — losing the user's input. Capturing once on open keeps the
  // dialog stable until it is explicitly closed.
  const [cancellationAppointment, setCancellationAppointment] = useState<AppointmentDto | undefined>(
    undefined
  );
  const [cancellationInitialSlotIds, setCancellationInitialSlotIds] = useState<
    string[] | undefined
  >(undefined);
  useEffect(() => {
    if (controller.isCancellationDialogOpen) {
      setCancellationAppointment((prev) => prev ?? lookedUpApp);
      setCancellationInitialSlotIds((prev) => {
        if (prev) return prev;
        const clickedId = controller.selectedAppointmentRef.current?.id;
        return clickedId ? [clickedId] : undefined;
      });
    } else {
      setCancellationAppointment(undefined);
      setCancellationInitialSlotIds(undefined);
    }
    // Capture only on the open/close transition; deliberately not reacting to
    // later `lookedUpApp` identity changes so cache churn cannot replace it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller.isCancellationDialogOpen]);

  // console.log("Cached appointment lookup debug info:", controller.selectedAppointmentRef.current, controller.clickedTimeSlot);

  // For the informing dialog
  const informedAppointment = informedSlot?.appointmentId
    ? lookupByAppointmentId(informedSlot.appointmentId)
    : undefined;
  const informedSlots = informedAppointment?.timeSlots?.length
    ? informedAppointment.timeSlots.map((ts: any) => ({
        id: ts?.id,
        start: ts?.start,
        end: ts?.end,
        informing: ts?.informing,
        rxNumber: ts?.therapyRx?.rx?.rxNumber,
        therapy: slotTherapy(ts)?.abbreviation,
      }))
    : informedSlot
    ? [
        {
          id: informedSlot.id,
          start: informedSlot.start,
          end: informedSlot.end,
          informing: informedSlot.informing,
          rxNumber: informedSlot.rx?.rxNumber,
          therapy: informedSlot.therapy?.abbreviation,
        },
      ]
    : undefined;

  return (
    <>
      {controller.clickedTimeSlot && (
        <ContextMenu
          data-context-menu-root
          open={controller.isOpen}
          event={controller.contextMenuEvent as any}
          timeSlot={controller.clickedTimeSlot}
          continuous={handlers.isContinuous}
          mouseY={controller.mousePosition.mouseY}
          mouseX={controller.mousePosition.mouseX}
          onClose={controller.closeContextMenu}
          onRemoveGoneFishing={controller.handleRemoveGoneFishing as any}
          onRemoveEventTimeSlot={controller.handleRemoveEventTimeSlot as any}
          onSplitLunchBreak={controller.handleSplitLunchBreak as any}
          onRemoveContinuousEventTimeSlot={controller.handleRemoveContinuousEventTimeSlot as any}
          onEditEventTitle={controller.handleEditEventTitle as any}
          onRemoveGroupTherapist={controller.handleRemoveGroupTherapist as any}
          onRemoveLeaveOfAbsence={controller.handleRemoveLeaveOfAbsence as any}
          onRemoveSimpleWorkingHours={controller.handleRemoveSimpleWorkingHours as any}
          onCancelDay={handlers.onCancelDayClick}
          onMoveEvent={controller.handleMoveEvent as any}
          lookupByAppointmentId={lookupByAppointmentId}
          onAddShelfEvent={controller.handleAddShelfEvent as any}
          onAddWaitListEvent={controller.handleWaitList as any}
          onCancelAppointment={controller.handleCancelAppointment}
          onCancelContinuousAppointment={controller.handleCancelContinuousAppointment as any}
          onUpdateCancelledAppointment={controller.handleUpdateCancelledAppointment as any}
          onToggleAttended={controller.handleAttended as any}
          onEditAppointment={controller.handleEditAppointment}
          onSetInformed={setInformedSlot}
          onCloseNotesDialog={controller.handleCloseNotesDialog}
          onAppointmentOverview={controller.handleAppointmentOverview}
          onOpenNotesDialog={controller.handleOpenNotesDialog}
        />
      )}

      {/* Dialog Components */}
      {controller.dialogOpen && lookedUpApp && (
        <TimeSlotAttendanceDialog
          appointment={lookedUpApp}
          dialogOpen={controller.dialogOpen}
          setDialogOpen={controller.setDialogOpen}
          isCalendarView={true}
          patientId={CalendarStore.selectedPatientId as number}
        />
      )}

      <SetInformedDialog
        open={!!informedSlot}
        onClose={() => setInformedSlot(null)}
        appointmentId={informedSlot?.appointmentId}
        appointmentStart={informedSlot?.start ? new Date(informedSlot.start) : undefined}
        projection={informedSlot?.informing}
        visitType={informedSlot?.visitType}
        patientName={informedSlot?.patient?.fullName}
        slots={informedSlots}
      />

      {controller.isCancellationDialogOpen && cancellationAppointment && (
        <CancelAppointmentDialog
          patientId={CalendarStore.selectedPatientId as number}
          isOpen={controller.isCancellationDialogOpen}
          onClose={controller.handleCloseCancellation}
          appointment={cancellationAppointment}
          initialSelectedTimeslotIds={cancellationInitialSlotIds}
        />
      )}

      {controller.waitListOpen && !handlers.isContinuous && (
        <WaitList
          event={(controller as any).waitListEvent as any}
          handleClose={controller.handleWaitListClose}
          waitListOpen={controller.waitListOpen}
          calendarEvents={handlers.calendarEvents}
          onOutdated={handlers.onOutdated}
        />
      )}

      {controller.isContinuousCancelationWarningOpen && (
        <WarningDialog
          title="Stornieren"
          content="Bist du sicher, dass du den Dauertermin stornieren möchtest?"
          open={controller.isContinuousCancelationWarningOpen}
          onClose={() => {controller.onCancelContinuousAppointmentConfirm(false)}}
          onContinue={() => {controller.onCancelContinuousAppointmentConfirm(true)}}
        />
      )}

      {/* Centralized deletion confirmation dialog (replaces native confirm()) */}
      {(controller as any).deletionDialogOpen && (
        <WarningDialog
          title="Löschen"
          content={(controller as any).deletionMessage}
          open={(controller as any).deletionDialogOpen}
          onClose={(controller as any).closeDeletionDialog}
          onContinue={(controller as any).confirmDeletion}
        />
      )}

      {/* Edit the title of an existing EventTimeSlot */}
      {(controller as any).editTitleDialogOpen && (
        <EditEventTitleDialog
          open={(controller as any).editTitleDialogOpen}
          initialTitle={(controller as any).editTitleEvent?.title ?? ""}
          onClose={(controller as any).closeEditTitleDialog}
          onSave={(controller as any).confirmEditTitle}
        />
      )}

      {controller.isAppointmentEditOpen && lookedUpApp && (
        <AppointmentEditCard
          open={controller.isAppointmentEditOpen}
          appointment={
            (handlers.isContinuous
              ? lookedUpApp
              : lookedUpApp) as AppointmentDto
          }
          onClose={controller.handleCloseEditAppointment}
          patientId={CalendarStore.selectedPatientId!}
          isContinous={handlers.isContinuous}
        />
      )}
      {controller.isEditCancellationDialogOpen && (
        <EditCancellationDialog
          open={controller.isEditCancellationDialogOpen}
          onClose={controller.handleCloseEditCancelledAppointment}
          appointment={lookedUpApp!}
          patientId={CalendarStore.selectedPatientId!}
        />
      )}

      {controller.overviewAppointmentPatientId && (
        <AppointmentOverviewModal
          open={controller.overviewAppointmentPatientId !== null}
          onClose={controller.handleCloseOverviewAppointment}
          patientId={controller.overviewAppointmentPatientId}
          selectedRxId={controller.overviewAppointmentRxId}
        />
      )}
    </>
  );
}));

ContextMenuWithController.displayName = "ContextMenuWithController";

// Export the controller hook for external usage
export { useContextMenuController } from "./useContextMenuController";
